import type { SupabaseClient } from "@supabase/supabase-js";
import { buildTrialExtractionPayload, getFunctionErrorMessage } from "@/lib/trial-site/trialExtractionPayload";

type AnySupabase = SupabaseClient<any, any, any>;
export type TrialExtractionChunkProgress = { current: number; total: number; label: string };

const SAFE_AI_CHUNK_CHARS = 42_000;
const MIN_SPLITTABLE_CHUNK_CHARS = 16_000;

const ARRAY_FIELDS = [
  "arms",
  "primary_endpoints",
  "secondary_endpoints",
  "exploratory_endpoints",
  "safety_considerations",
  "dosing_schedule",
  "visit_schedule",
  "site_payment_schedule",
  "patient_stipend_schedule",
  "inclusion_criteria",
  "exclusion_criteria",
  "ecrf_forms",
  "ecoa_instruments",
  "equipment_and_tools",
  "biomarkers",
  "sample_collections",
] as const;

function compactText(value: string): string {
  return value.replace(/\r\n/g, "\n").replace(/[\t ]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
}

function splitLargeBlock(block: string, maxChars = SAFE_AI_CHUNK_CHARS): string[] {
  const chunks: string[] = [];
  let rest = block.trim();

  while (rest.length > maxChars) {
    const searchFrom = Math.floor(maxChars * 0.65);
    const paragraphCut = rest.lastIndexOf("\n\n", maxChars);
    const sentenceCut = rest.lastIndexOf(". ", maxChars);
    const cut = paragraphCut > searchFrom ? paragraphCut : sentenceCut > searchFrom ? sentenceCut + 1 : maxChars;
    chunks.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }

  if (rest) chunks.push(rest);
  return chunks;
}

export function buildTrialExtractionChunks(text: string, filename?: string): string[] {
  const focused = compactText(buildTrialExtractionPayload(text, filename));
  if (focused.length <= SAFE_AI_CHUNK_CHARS) return [focused];

  const pageParts = focused.split(/(?=\n?--- PAGE \d+ ---)/g).map((p) => p.trim()).filter(Boolean);
  const sourceParts = pageParts.length > 1 ? pageParts : focused.split(/\n\n(?=--- DOCUMENT EXCERPT \d+)/g).map((p) => p.trim()).filter(Boolean);
  const chunks: string[] = [];
  let current = "";

  for (const part of sourceParts.length ? sourceParts : [focused]) {
    if (part.length > SAFE_AI_CHUNK_CHARS) {
      if (current) {
        chunks.push(current.trim());
        current = "";
      }
      chunks.push(...splitLargeBlock(part));
      continue;
    }

    const next = current ? `${current}\n\n${part}` : part;
    if (next.length > SAFE_AI_CHUNK_CHARS && current) {
      chunks.push(current.trim());
      current = part;
    } else {
      current = next;
    }
  }

  if (current) chunks.push(current.trim());
  return chunks.filter((chunk) => chunk.length >= 200);
}

function arrayKey(field: string, item: unknown): string {
  if (typeof item === "string") return item.trim().toLowerCase();
  if (!item || typeof item !== "object") return JSON.stringify(item);
  const obj = item as Record<string, unknown>;
  if (field === "visit_schedule") return `${obj.visit_code || ""}|${obj.visit_name || ""}|${obj.day || ""}`.toLowerCase();
  if (field === "arms") return String(obj.name || obj.arm_name || JSON.stringify(obj)).toLowerCase();
  if (field === "dosing_schedule") return `${obj.visit || ""}|${obj.day || ""}|${obj.product || ""}|${obj.dose || ""}`.toLowerCase();
  if (field === "site_payment_schedule") return `${obj.fee_name || ""}|${obj.fee_category || ""}|${obj.visit_code || ""}`.toLowerCase();
  if (field === "patient_stipend_schedule") return `${obj.stipend_name || ""}|${obj.stipend_type || ""}|${obj.visit_code || ""}`.toLowerCase();
  if (field === "ecrf_forms") return `${obj.form_name || ""}|${obj.form_type || ""}`.toLowerCase();
  if (field === "ecoa_instruments") return `${obj.instrument_code || ""}|${obj.instrument_name || ""}`.toLowerCase();
  if (field === "equipment_and_tools") return `${obj.item || ""}|${obj.category || ""}`.toLowerCase();
  if (field === "sample_collections") return `${obj.sample_type || ""}|${obj.visit_code || ""}`.toLowerCase();
  return JSON.stringify(obj).toLowerCase();
}

export function mergeTrialExtractions(parts: Array<Record<string, any>>): Record<string, any> {
  const merged: Record<string, any> = {};

  for (const part of parts) {
    if (!part || typeof part !== "object") continue;

    for (const [key, value] of Object.entries(part)) {
      if (value == null) continue;

      if ((ARRAY_FIELDS as readonly string[]).includes(key)) {
        const existing = Array.isArray(merged[key]) ? merged[key] : [];
        const incoming = Array.isArray(value) ? value : [];
        const seen = new Set(existing.map((item: unknown) => arrayKey(key, item)));
        for (const item of incoming) {
          const dedupeKey = arrayKey(key, item);
          if (dedupeKey && !seen.has(dedupeKey)) {
            existing.push(item);
            seen.add(dedupeKey);
          }
        }
        merged[key] = existing;
        continue;
      }

      if (key === "confidence" && typeof value === "object") {
        merged.confidence = { ...(merged.confidence ?? {}), ...value };
        continue;
      }

      if (key === "randomization" && typeof value === "object") {
        merged.randomization = { ...(merged.randomization ?? {}), ...value };
        continue;
      }

      if (typeof value === "string" && value.trim()) {
        if (!merged[key]) merged[key] = value;
      } else if (typeof value === "number" || typeof value === "boolean") {
        if (merged[key] == null) merged[key] = value;
      } else if (typeof value === "object" && Object.keys(value as Record<string, unknown>).length) {
        merged[key] = { ...(merged[key] ?? {}), ...value };
      }
    }
  }

  return merged;
}

function isRetryableTimeout(message: string): boolean {
  return /timeout|idle|reset|closed|network|failed to fetch|message completed|gateway unreachable/i.test(message);
}

async function invokeExtractionChunk(
  supabase: AnySupabase,
  textContent: string,
  filename: string,
  label: string,
): Promise<Record<string, any>> {
  const { data, error } = await supabase.functions.invoke("extract-trial-metadata", {
    body: { text_content: textContent, filename: `${filename} — ${label}` },
  });
  if (error) throw new Error(await getFunctionErrorMessage(error, "AI extraction request failed"));
  if (data?.error) throw new Error(String(data.error));
  return data?.extracted ?? {};
}

async function invokeWithFallbackSplit(
  supabase: AnySupabase,
  chunk: string,
  filename: string,
  label: string,
  depth = 0,
): Promise<Record<string, any>[]> {
  try {
    return [await invokeExtractionChunk(supabase, chunk, filename, label)];
  } catch (error: any) {
    const message = error?.message ?? String(error);
    if (depth >= 2 || chunk.length < MIN_SPLITTABLE_CHUNK_CHARS || !isRetryableTimeout(message)) throw error;

    const halves = splitLargeBlock(chunk, Math.ceil(chunk.length / 2));
    const results: Record<string, any>[] = [];
    for (let i = 0; i < halves.length; i += 1) {
      results.push(...await invokeWithFallbackSplit(supabase, halves[i], filename, `${label}.${i + 1}`, depth + 1));
    }
    return results;
  }
}

export async function extractTrialMetadataInChunks(
  supabase: AnySupabase,
  text: string,
  filename: string,
  options: { onProgress?: (progress: TrialExtractionChunkProgress) => void } = {},
): Promise<Record<string, any>> {
  const chunks = buildTrialExtractionChunks(text, filename);
  const results: Record<string, any>[] = [];

  for (let i = 0; i < chunks.length; i += 1) {
    const label = chunks.length === 1 ? "full document" : `chunk ${i + 1}/${chunks.length}`;
    options.onProgress?.({ current: i + 1, total: chunks.length, label });
    const textContent = chunks.length === 1
      ? chunks[i]
      : `This is ${label} from ${filename}. Extract only facts present in this chunk; do not infer missing sections.\n\n${chunks[i]}`;
    results.push(...await invokeWithFallbackSplit(supabase, textContent, filename, label));
  }

  return mergeTrialExtractions(results);
}